/**
 * Build a solve request for the local (WASM) kernel from a Build + GenConfig.
 *
 * The server normally does this shaping in `sanitize_generation_req`. Running the
 * solver in the browser means doing it here instead — the frontend already holds
 * everything needed, so this is a translation, not new logic.
 *
 * Characteristic ids are shared: `st.js` and the backend's `Characs` use the same
 * numbering (AP=1, MP=23, …), so no mapping table is required. What does have to
 * match exactly is CHARAC_IDS below — the kernel's dense array order.
 */
import * as st from '../st';
import { spellWeightKey, WEAPON_WEIGHT_KEY } from '../criteriaWeights';
import { BEST_WEAPON_METRIC_DEFAULT } from '../bestWeaponMetric';
import {
    FM_EXO_IDS, lineKind, MAGING_KIND, magingBonusExcess, magingDelta, transcendenceBudget,
} from '../itemMaging';

/**
 * Dense characteristic order used by the packed kernel (`solver-core/src/charac.rs`).
 * base_stats is indexed by position in this array, so it must stay byte-identical
 * to the Rust constant — a mismatch silently scores the wrong stats.
 */
export const CHARAC_IDS = [
    5, 11, 1, 23, 19, 26, 44, 18, 49, 10, 15, 13, 14, 25, 12, 79, 78, 82, 27, 83, 28, 50,
    48, 40, 16, 92, 88, 89, 90, 91, 86, 84, 125, 120, 123, 122, 150, 37, 33, 34, 35, 36, 87,
    85, 124, 121, 141, 142, 58, 54, 55, 56, 57,
];

/** AP and MP are always hard targets; every other stat is soft. */
const HARD_IDS = new Set([st.AP_ID, st.MP_ID]);

/**
 * Characteristics that name an element, for narrowing the weapon pool.
 *
 * Both halves of each pair: asking for Intelligence and asking for % Fire
 * damage are the same statement about which weapon could serve this build.
 * Ids mirror `_ELEM_PAIR` in `gen/engine/spell_scoring.py`.
 */
const ELEMENT_BY_CHARAC = {
    [st.STRENGTH_ID]: 'earth',
    [st.INTELLIGENCE_ID]: 'fire',
    [st.CHANCE_ID]: 'water',
    [st.AGILITY_ID]: 'air',
    88: 'earth',
    89: 'fire',
    90: 'water',
    91: 'air',
};

/** Elements the stat criteria name, as a list the pool can narrow on. */
function requestedElements({ targets, maximize }) {
    const out = new Set();
    [...Object.keys(targets || {}), ...Object.keys(maximize || {})].forEach((id) => {
        const el = ELEMENT_BY_CHARAC[Number(id)];
        if (el) out.add(el);
    });
    return [...out];
}

let weightById = null;

/** Per-characteristic scoring weight, from the same table the UI renders. */
/** `{ lineKey: n }` for this criterion's lines read below their maximum, or null. */
function criterionCharges(build, criterion) {
    const counts = typeof build?.getSpellLineCharges === 'function'
        ? build.getSpellLineCharges(criterion?.spell_id)
        : {};
    const keys = Array.isArray(criterion?.line_keys) ? criterion.line_keys : [];
    const out = {};
    for (const key of keys) {
        if (counts[key] != null) out[key] = counts[key];
    }
    return Object.keys(out).length ? out : null;
}

function weightFor(id) {
    if (weightById === null) {
        weightById = new Map();
        (st.STATS || []).forEach((s) => {
            if (s && Number.isFinite(Number(s.id))) weightById.set(Number(s.id), Number(s.weight ?? 1));
        });
    }
    const w = weightById.get(Number(id));
    return Number.isFinite(w) ? w : 1;
}

/** Characteristic scrolls give a flat +100 to each of these. */
const SCROLL_IDS = [
    st.VITALITY_ID, st.STRENGTH_ID, st.INTELLIGENCE_ID,
    st.CHANCE_ID, st.AGILITY_ID, st.WISDOM_ID,
];
const SCROLL_AMOUNT = 100;

/** The exos the forgemagie menu asks the run to place, +1 each in its base stats. */
function placedExoBonus(build) {
    const ids = Array.isArray(build?.fmExos) ? build.fmExos : [];
    return Object.fromEntries(FM_EXO_IDS.filter((id) => ids.includes(id)).map((id) => [id, 1]));
}

/**
 * The exos the solver must find items for, and the locked slots that cannot
 * take one. An exo is a line on a real item: the build is only valid if each
 * one fits an item that does not already carry that characteristic. One
 * already on a locked item is placed; a locked item already maged takes no
 * other (its budget is spoken for, and the user left it as it is).
 */
function exoPlacement(build, placed, lockedSlots) {
    const onLocked = new Set();
    const blocked = [];
    lockedSlots.forEach((slot) => {
        const lines = typeof build?.getItemMaging === 'function' ? build.getItemMaging(slot) : null;
        if (!lines) return;
        blocked.push(slot);
        const item = build.items?.[slot];
        Object.entries(lines).forEach(([id, value]) => {
            if (lineKind(item, id, value) === MAGING_KIND.EXO) onLocked.add(Number(id));
        });
    });
    return {
        exos: FM_EXO_IDS.filter((id) => placed.includes(id) && !onLocked.has(id)),
        exo_blocked_slots: blocked,
    };
}

/**
 * Character stats before any gear or invested points, as a dense array in
 * CHARAC_IDS order: natural stats for the level, plus scrolls, plus exos.
 *
 * All three parts matter. Natural stats alone are wrong by +100 on each of the
 * five main characteristics and by +1 AP/MP/Range — checked against Python's
 * `get_total_character_stats`, which is what the solver actually scores against.
 *
 * `exos` is a map of characteristic id -> bonus count: the exos the run places.
 */
export function baseStatsArray(lvl, { hasScrolls = true, exos = null } = {}) {
    const natural = st.getNaturalStats(lvl) || {};
    const byId = new Map();
    CHARAC_IDS.forEach((id) => byId.set(id, Number(natural[id]) || 0));

    if (hasScrolls) {
        SCROLL_IDS.forEach((id) => {
            if (byId.has(id)) byId.set(id, byId.get(id) + SCROLL_AMOUNT);
        });
    }

    Object.entries(exos || {}).forEach(([idStr, count]) => {
        const id = Number(idStr);
        const n = Number(count) || 0;
        if (byId.has(id) && n) byId.set(id, byId.get(id) + n);
    });

    return CHARAC_IDS.map((id) => byId.get(id) || 0);
}

/**
 * A locked item's overmage and exo lines, added to the base stats in place.
 * The kernel knows items only as packed rows, which carry the item as
 * dropped; a locked slot is always worn, so its maging is as good as base
 * stats. An unlocked maged item gets no such credit — the solver is free to
 * replace it, and scores it as dropped if it keeps it. The +1 AP/MP/Range
 * forgemagie limit is held against the character's exos, as the sheet does.
 */
/**
 * What spell criteria count on top of the base stats, in CHARAC_IDS order, or
 * null when nothing: the kernel adds it to the sheet for spell and weapon terms
 * only (`spell_stat_bonus`, solver-core `SpellCriterion::stat_bonus`).
 *
 * The base stats carry the boosts when stat criteria use them, so spells need
 * the difference: the boosts when only spells use them — less the packed
 * default passives of the picked items, which the kernel still counts there —
 * and the reverse when only stat criteria do.
 */
function spellBoostBonus(build, useBoostsForStats, useBoostsForSpells, passiveOverrides) {
    if (useBoostsForStats === useBoostsForSpells || typeof build?.getSolverBoostEffects !== 'function') return null;
    const sign = useBoostsForSpells ? 1 : -1;
    const bonus = CHARAC_IDS.map(() => 0);
    const add = (effects, factor) => Object.entries(effects || {}).forEach(([id, value]) => {
        const idx = CHARAC_IDS.indexOf(Number(id));
        if (idx >= 0) bonus[idx] += factor * (Number(value) || 0);
    });
    add(build.getSolverBoostEffects(), sign);
    if (typeof build.getPickedItemPassiveDefaults === 'function') {
        add(build.getPickedItemPassiveDefaults(passiveOverrides), -sign);
    }
    return bonus.some((v) => v !== 0) ? bonus : null;
}

/**
 * The picked boosts, added to the base stats in place, when the generator
 * setting "Utiliser les boosts pour atteindre les critères de stats" is on: the
 * solver then treats them as natural stats of the character. Gear that re-buys them is then wasted — a
 * build with 26 % Critique from boosts needs 26 fewer from its items to reach a
 * cap. Which boosts count is Build.getSolverBoostEffects, which the displayed
 * score reads too.
 */
function addBoosts(baseStats, build) {
    const effects = typeof build?.getSolverBoostEffects === 'function' ? build.getSolverBoostEffects() : {};
    Object.entries(effects).forEach(([id, value]) => {
        const idx = CHARAC_IDS.indexOf(Number(id));
        if (idx >= 0) baseStats[idx] += Number(value) || 0;
    });
}

function addLockedMaging(baseStats, build, lockedSlots, exos) {
    const add = (id, value) => {
        const idx = CHARAC_IDS.indexOf(Number(id));
        if (idx >= 0) baseStats[idx] += value;
    };
    const deltas = [];
    lockedSlots.forEach((slot) => {
        const item = Array.isArray(build?.items) ? build.items[slot] : null;
        const lines = typeof build?.getItemMaging === 'function' ? build.getItemMaging(slot) : null;
        if (!item || !lines) return;
        const delta = magingDelta(item, lines);
        deltas.push(delta);
        Object.entries(delta).forEach(([id, value]) => add(id, value));
    });
    Object.entries(magingBonusExcess(exos, deltas)).forEach(([id, excess]) => add(id, -excess));
}

/**
 * Split the UI's criteria into the kernel's finite targets, maximize rows and
 * floors.
 *
 * Mirrors `sanitize_request_targets`: a positive `min` makes a finite row and wins
 * over `maximize` for the same stat; AP and MP are forced hard. A floor (a target
 * at the stat's minimum, `floor: true`) travels on its own: it is a penalty
 * while missed and nothing otherwise, never a term — see `score::floor_penalty`.
 */
export function splitTargets(solverTargets) {
    const targets = {};
    const maximize = {};
    const floors = {};
    Object.entries(solverTargets || {}).forEach(([idStr, rule]) => {
        const id = Number(idStr);
        if (!Number.isFinite(id) || !rule) return;
        const min = Number(rule.min) || 0;
        const weight = weightFor(id);
        // `importance` is the weights column's multiplier and rides beside
        // `weight`, never inside it — see Build.weightRule. Omitted at 1 so an
        // untouched request is byte-identical to what it was before weights.
        const importance = Number(rule.importance);
        const extra = Number.isFinite(importance) && importance !== 1 ? { importance } : null;
        if (rule.floor === true) {
            floors[String(id)] = { min: Number(rule.min) || 0, weight, ...extra };
        } else if (min > 0) {
            targets[String(id)] = {
                min, weight, hard: rule.hard === true || HARD_IDS.has(id), ...extra,
            };
        } else if (rule.maximize) {
            maximize[String(id)] = { weight, ...extra };
        }
    });
    return { targets, maximize, floors };
}

/**
 * Assemble the request the kernel's `dofgen_wasm_solve` expects.
 *
 * Returns `{ request, unsupported }`. `unsupported` lists reasons this request
 * cannot be solved locally yet, so the caller can fall back to the server rather
 * than silently solving something different from what the user asked for.
 */
export function toKernelRequest(build, genConfig) {
    const lvl = Number(build?.lvl) || 200;
    const settings = typeof genConfig?.getSettings === 'function' ? genConfig.getSettings() : {};
    const { targets, maximize, floors } = splitTargets(
        typeof build?.getSolverTargets === 'function' ? build.getSolverTargets() : {},
    );

    const lockedSlots = Object.keys(settings.lockedSlots || {})
        .map(Number)
        .filter(Number.isFinite);
    const lockedItems = {};
    lockedSlots.forEach((slot) => {
        const item = Array.isArray(build?.items) ? build.items[slot] : null;
        const id = item && Number(item.ankama_id ?? item.id);
        if (Number.isFinite(id) && id > 0) lockedItems[String(slot)] = id;
    });

    const excludedTypes = Object.entries(settings.typeFilters || {})
        .filter(([, included]) => !included)
        .map(([typeId]) => Number(typeId))
        .filter(Number.isFinite);

    // Spells, weapons and passive overrides are all solvable by the kernel — a
    // weapon is just a spell criterion with `is_weapon` set. What the browser
    // cannot do is build those structures: they come from spell levels, effect
    // lines and item passives that live in the database. The server shapes them
    // on the authorise call this solve already makes, and they are merged in
    // below. Nothing here needs to fall back on their account.
    const unsupported = [];
    const spellIds =
        typeof build?.getMaximizeSpellIds === 'function' ? build.getMaximizeSpellIds() : [];
    const spellCriteria =
        typeof build?.getMaximizeSpellCriteria === 'function'
            ? build.getMaximizeSpellCriteria()
            : spellIds;
    const weaponItem = typeof build?.getWeapon === 'function' ? build.getWeapon() : null;
    const weaponId = weaponItem?.id != null && Number.isFinite(Number(weaponItem.id))
        ? Number(weaponItem.id)
        : null;

    /** What the server has to resolve before the kernel can be given this request. */
    const shaping = {
        lvl,
        // Not used for shaping — it is what the activity feed labels the run
        // with. The shaping payload carries spells but never the stat targets,
        // so the server cannot count criteria for a browser solve; counting
        // here is the only place that sees both halves.
        n_criteria:
            Object.keys(targets).length
            + Object.keys(maximize).length
            + (spellCriteria || []).length
            + (typeof build?.getWeaponSolverTargetCount === 'function'
                ? build.getWeaponSolverTargetCount()
                : 0),
        // Each entry carries its own `importance` (the weights column). The
        // server shapes these into kernel spell_criteria and copies it across;
        // it is never folded into the term — see score::weighted_power_mean.
        spells_criteria: (spellCriteria || []).map((c) => {
            const key = spellWeightKey(c);
            const w = key && typeof build?.getCriterionWeight === 'function'
                ? Number(build.getCriterionWeight(key))
                : 1;
            const importance = Number.isFinite(w) ? w : 1;
            // A charged line read below its maximum travels with its count; the
            // server re-reads the line at it (spell_scoring.charged_row_at).
            const charges = typeof c === 'object' ? criterionCharges(build, c) : null;
            if (importance === 1 && !charges) return c;
            const entry = typeof c === 'number' ? { spell_id: c } : { ...c };
            if (importance !== 1) entry.importance = importance;
            if (charges) entry.charges = charges;
            return entry;
        }),
        weapon_importance: typeof build?.getCriterionWeight === 'function'
            ? (Number(build.getCriterionWeight(WEAPON_WEIGHT_KEY)) || 1)
            : 1,
        weapon_criteria: build?.maximizeWeapon === true && weaponId != null ? weaponId : null,
        // Find the best weapon. The server answers with a criterion per
        // candidate and the scale they are ranked on; both are database reads.
        best_weapon: build?.maximizeBestWeapon === true,
        best_weapon_metric: typeof build?.getBestWeaponMetric === 'function'
            ? build.getBestWeaponMetric()
            : BEST_WEAPON_METRIC_DEFAULT,
        // Which elements the *stat* half of the request scores. The server
        // cannot work this out: the shaping payload deliberately carries no
        // stat targets. It reads the spells' elements off its own compiled
        // criteria and unions the two. Naming none means "every element",
        // which is the whole pool and every forgemage option with it.
        best_weapon_elements: build?.maximizeBestWeapon === true
            ? requestedElements({ targets, maximize })
            : [],
        best_weapon_importance: typeof build?.getCriterionWeight === 'function'
            ? (Number(build.getCriterionWeight(WEAPON_WEIGHT_KEY)) || 1)
            : 1,
        weapon_maging_element:
            typeof build?.getWeaponMagingElement === 'function'
                ? build.getWeaponMagingElement()
                : null,
        // The heal half of the same forgemage, chosen apart from the damage one.
        // It shapes the criterion the same way — the primary characteristic a
        // neutral "soins" row scales with — so it travels on the same call.
        weapon_maging_heal_element:
            typeof build?.getWeaponMagingHealElement === 'function'
                ? build.getWeaponMagingHealElement()
                : null,
        // A picked item passive is a natural stat (addBoosts) when stat criteria
        // use boosts; its packed default goes.
        passive_overrides: settings.useBoostsForStats === true && typeof build?.getSolverPassiveOverrides === 'function'
            ? build.getSolverPassiveOverrides(settings.passiveOverrides || {})
            : (settings.passiveOverrides || {}),
        // Which items are legendary is a database column the packed dataset does
        // not carry, so the server answers with the ids and they join the
        // blacklist — the one exclusion mechanism the kernel already has.
        exclude_legendary: settings.allowLegendary === false,
    };

    const exos = placedExoBonus(build);
    const baseStats = baseStatsArray(lvl, {
            // Scrolls live on the Build (the stats panel's scroll toggle writes
            // them), not in genConfig — read them there or the kernel scores a
            // +100-per-characteristic character the panel is not showing.
            hasScrolls: typeof build?.isFullyScrolled === 'function'
                ? build.isFullyScrolled()
                : settings.hasScrolls !== false,
            exos,
        });
    addLockedMaging(baseStats, build, lockedSlots, exos);
    const useBoostsForStats = settings.useBoostsForStats === true;
    const useBoostsForSpells = settings.useBoostsForSpells !== false;
    if (useBoostsForStats) addBoosts(baseStats, build);
    const spellStatBonus = spellBoostBonus(build, useBoostsForStats, useBoostsForSpells, settings.passiveOverrides || {});

    const request = {
        lvl,
        base_stats: baseStats,
        targets,
        maximize_targets: maximize,
        floors,
        spell_criteria: [],
        ...(spellStatBonus ? { spell_stat_bonus: spellStatBonus } : {}),
        locked_slots: lockedSlots,
        locked_items: lockedItems,
        blacklist: (settings.blacklist || []).map(Number).filter(Number.isFinite),
        excluded_types: excludedTypes,
        allow_power: settings.allowPower !== false,
        crit_dam_counts_as_damage: settings.critDamCountsAsDamage === true,
        defense_share: Number(genConfig?.defenseShare) || 0,
        position: genConfig?.position || st.POSITION_NONE,
        // How many items may wear a transcendence rune; 0 keeps them out. A
        // locked item already transcended is one of them.
        rune_budget: typeof build?.isTranscendenceOn === 'function' && build.isTranscendenceOn()
            ? Math.max(0, transcendenceBudget(exos)
                - lockedSlots.filter((slot) => build?.itemMaging?.[slot]?.trans != null).length)
            : 0,
        ...exoPlacement(build, Object.keys(exos).map(Number), lockedSlots),
        exo_rings_only: settings.exoRingsOnly === true,
    };

    return { request, unsupported, shaping };
}
